import { useEffect } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

const AVAILABLE_COLORS = [
  { value: "#d7fcf0", label: "Verde Menta" },
  { value: "#b9fee8", label: "Verde Agua" },
  { value: "#f79dad", label: "Rosa Claro" },
  { value: "#ffcfd7", label: "Rosa Pastel" },
  { value: "#bdb3ff", label: "Púrpura Claro" },
  { value: "#dcd7fc", label: "Lavanda" },
];

interface ColorPickerProps {
  chartType: string;
  colors: string[];
  onChange: (colors: string[]) => void;
}

// Determinar cuántos colores necesita cada tipo de gráfico
function getRequiredColorsCount(chartType: string): number {
  switch (chartType) {
    case "pie":
      return 3; // Manager, Messenger, Mentor
    case "pie-only-color":
      return 3;
    case "bar-referral":
      return 2; // Usuario, Referido
    case "pie-update":
      return 4;
    case "radial":
      return 3; // Por categoría
    case "bar":
      return 1; // Un solo color principal
    default:
      return 1;
  }
}

export function ColorPicker({ chartType, colors, onChange }: ColorPickerProps) {
  const requiredCount = getRequiredColorsCount(chartType);

  const handleColorSelect = (colorValue: string, index: number) => {
    const newColors = [...colors];
    newColors[index] = colorValue;
    onChange(newColors);
  };

  // Initialize colors if needed - moved to useEffect to avoid setState during render
  useEffect(() => {
    if (colors.length < requiredCount) {
      const newColors = [...colors];
      while (newColors.length < requiredCount) {
        newColors.push(AVAILABLE_COLORS[newColors.length % AVAILABLE_COLORS.length].value);
      }
      onChange(newColors);
    }
  }, [colors.length, requiredCount, onChange]);

  const colorLabels = getColorLabels(chartType, requiredCount);

  return (
    <div className="space-y-4">
      {Array.from({ length: requiredCount }).map((_, index) => (
        <div key={index} className="space-y-2">
          <label className="text-sm font-medium">
            Color {index + 1}
            {colorLabels[index] && ` - ${colorLabels[index]}`}
          </label>
          <div className="flex gap-2">
            {AVAILABLE_COLORS.map((color) => (
              <button
                key={color.value}
                type="button"
                className={cn(
                  "w-10 h-10 rounded-md border-2 transition-all relative",
                  colors[index] === color.value
                    ? "border-primary scale-110"
                    : "border-gray-300 hover:scale-105"
                )}
                style={{ backgroundColor: color.value }}
                onClick={() => handleColorSelect(color.value, index)}
                title={color.label}
              >
                {colors[index] === color.value && (
                  <Check className="h-5 w-5 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-gray-800" />
                )}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function getColorLabels(chartType: string, count: number): string[] {
  switch (chartType) {
    case "pie":
      return ["Manager", "Messenger", "Mentor"];
    case "bar-referral":
      return ["Usuario", "Referido"];
    case "pie-update":
      return ["Sector 1", "Sector 2", "Sector 3", "Sector 4"];
    case "radial":
      return Array.from({ length: count }, (_, i) => `Categoría ${i + 1}`);
    default:
      return Array.from({ length: count }, (_, i) => ``);
  }
}
